import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DemoLockNotice, DemoLockWrap } from "../components/DemoLock";

export default function Mitigate() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`mitigate_page.${k}`, o);
  const [params] = useSearchParams();
  const preProject = params.get("project") || "";
  const [projects, setProjects] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [certs, setCerts] = useState([]);
  const [form, setForm] = useState({ project_id: preProject, tons: 1, beneficiary: "", purpose: "Voluntary emission mitigation", attribution: "self" });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const load = async () => {
    setProjects((await api.get("/projects")).data || []);
    setWallet((await api.get("/wallet")).data);
    setCerts((await api.get("/mitigation/certificates")).data || []);
  };
  useEffect(() => { load(); }, []);

  const retire = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mitigation/retire", { ...form, tons: Number(form.tons) });
      toast.success(`Certificate ${data.id} issued`);
      setIssued(data);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const heldTons = wallet?.ucs_tons || 0;

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="label-tag mb-4">{tr("tag")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title")}</h1>
        <p className="text-ink2 mt-3 max-w-2xl">{t("mitigate_page.subtitle", "Retirement permanently removes UCS tons from circulation against a chosen project. A blockchain-anchored certificate is issued for your records and ESG reporting.")}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 pb-24">
        <div className="lg:col-span-5 space-y-4">
          <DemoLockNotice action="retire" />
          <DemoLockWrap>
            <form onSubmit={retire} className="border border-ink p-7 space-y-5" data-testid="mitigate-form">
          <div className="flex items-center justify-between">
            <div className="label-tag">{tr("available_to_retire")}</div>
            <div className="mono-num text-2xl">{heldTons.toFixed(3)} tCO₂</div>
          </div>

          <div>
            <label className="label-tag block mb-2">{tr("project")}</label>
            <select required value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))} className="input-field" data-testid="mitigate-project">
              <option value="">— {tr("select_project")} —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.region}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tag block mb-2">{tr("tons_to_retire")}</label>
            <input type="number" min="0.001" step="0.001" max={heldTons} required value={form.tons} onChange={(e) => setForm((f) => ({ ...f, tons: e.target.value }))} className="input-field" data-testid="mitigate-tons" />
          </div>
          <div>
            <label className="label-tag block mb-2">{tr("attribution")}</label>
            <div className="grid grid-cols-2 gap-2" data-testid="mitigate-attribution">
              <button type="button"
                onClick={() => setForm((f) => ({ ...f, attribution: "self" }))}
                data-testid="attr-self"
                className={`border px-3 py-3 text-left ${form.attribution === "self" ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"}`}>
                <div className="text-xs font-mono uppercase tracking-wider">{tr("retire_self")}</div>
                <div className={`text-[11px] mt-1 ${form.attribution === "self" ? "text-bg/70" : "text-ink2"}`}>{t("mitigate_page.retire_self_hint", "Claim the offset on your ESG report.")}</div>
              </button>
              <button type="button"
                onClick={() => setForm((f) => ({ ...f, attribution: "platform" }))}
                data-testid="attr-platform"
                className={`border px-3 py-3 text-left ${form.attribution === "platform" ? "border-ink bg-ink text-bg" : "border-line hover:border-ink"}`}>
                <div className="text-xs font-mono uppercase tracking-wider">{tr("retire_greenbelt")}</div>
                <div className={`text-[11px] mt-1 ${form.attribution === "platform" ? "text-bg/70" : "text-ink2"}`}>{t("mitigate_page.retire_greenbelt_hint", "GreenBelt is recorded as the claimant.")}</div>
              </button>
            </div>
          </div>
          <div>
            <label className="label-tag block mb-2">{tr("beneficiary")}</label>
            <input
              required={form.attribution === "self"}
              disabled={form.attribution === "platform"}
              value={form.attribution === "platform" ? "GreenBelt Platform (on behalf of buyer)" : form.beneficiary}
              onChange={(e) => setForm((f) => ({ ...f, beneficiary: e.target.value }))}
              className="input-field disabled:opacity-60"
              data-testid="mitigate-beneficiary" />
          </div>
          <div>
            <label className="label-tag block mb-2">{tr("purpose")}</label>
            <input value={form.purpose} onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))} className="input-field" data-testid="mitigate-purpose" />
          </div>
          <button type="submit" disabled={busy || heldTons <= 0} className="btn-primary w-full disabled:opacity-50" data-testid="mitigate-submit">
            {busy ? tr("issuing") : `${tr("retire_and_issue")} →`}
          </button>
            </form>
          </DemoLockWrap>
        </div>

        <div className="lg:col-span-7 space-y-6">
          {issued && (
            <div className="border border-leaf p-6 bg-surface" data-testid="cert-issued">
              <div className="label-tag text-leaf">{tr("certificate_issued")}</div>
              <div className="font-serif text-2xl mt-1">{issued.id}</div>
              <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                <div><div className="label-tag">{tr("beneficiary")}</div><div>{issued.beneficiary}</div></div>
                <div><div className="label-tag">{t("mitigate_page.tons", "Tons")}</div><div className="mono-num">{issued.tons_retired}</div></div>
                <div><div className="label-tag">{tr("project")}</div><div>{issued.project_name}</div></div>
                <div><div className="label-tag">{t("mitigate_page.bmv_unit", "BMV Unit")}</div><div className="font-mono text-xs">{issued.bmv_unit_id}</div></div>
              </div>
              <Link to={`/verify/${issued.id}`} className="text-xs font-mono underline mt-4 inline-block">{t("mitigate_page.verify_publicly", "VERIFY PUBLICLY")} →</Link>
            </div>
          )}

          <div className="border border-line bg-surface">
            <div className="p-5 border-b border-line label-tag">{t("mitigate_page.your_certificates", "Your certificates")}</div>
            {certs.length === 0 ? (
              <div className="p-10 text-center text-ink2 text-sm">{t("mitigate_page.no_retirements", "No retirements yet.")}</div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-line text-left">
                  <th className="px-4 py-3 label-tag">{t("mitigate_page.col_id", "ID")}</th>
                  <th className="px-4 py-3 label-tag">{tr("project")}</th>
                  <th className="px-4 py-3 label-tag">{t("mitigate_page.tons", "Tons")}</th>
                  <th className="px-4 py-3 label-tag">{t("mitigate_page.col_date", "Date")}</th>
                </tr></thead>
                <tbody>
                  {certs.map((c) => (
                    <tr key={c.id} className="border-b border-line/60" data-testid={`cert-${c.id}`}>
                      <td className="px-4 py-3"><Link to={`/verify/${c.id}`} className="font-mono text-xs underline">{c.id}</Link></td>
                      <td className="px-4 py-3">{c.project_name}</td>
                      <td className="px-4 py-3 mono-num">{c.tons_retired}</td>
                      <td className="px-4 py-3 mono-num text-xs text-ink2">{new Date(c.issued_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
